import { useState } from "react";

import { Button } from "atoms/button/Button";
import { TextButton } from "atoms/text-button/TextButton";

import { PersonalisationHeading } from "./PersonalisationHeading";
import { PersonalisationProgressBar } from "./PersonalisationProgressBar";
import { PersonalisationScreenLayout } from "./PersonalisationScreenLayout";
import { SingleChoiceTagPicker } from "./SingleChoiceTagPicker";

interface HeritageItem {
  id: string;
  label: string;
}

interface HeritageProps {
  options?: HeritageItem[];
  onContinue?: (selection: string) => void;
  onSkip?: () => void;
  selectedColor?: string;
  continueButtonText?: string;
  skipText?: string;
  progressPercentage: number;
  onBack?: () => void;
  onClose?: () => void;
  initialSelectedId?: string | null;
}

const defaultHeritageOptions: HeritageItem[] = [
  { id: "WhiteOrEuropean", label: "White / European" },
  { id: "HispanicOrLatino", label: "Hispanic / Latino" },
  {
    id: "BlackAfricanOrAfricanAmerican",
    label: "Black / African or African American",
  },
  { id: "Asian", label: "Asian" },
  {
    id: "MiddleEasternOrNorthAfrican",
    label: "Middle Eastern / North African",
  },
  {
    id: "NativeHawaiianOrPacificIslander",
    label: "Native Hawaiian / Pacific Islander",
  },
  {
    id: "NativeAmericanOrAlaskaNative",
    label: "Native American / Alaska Native",
  },
];

export const Heritage = ({
  options = defaultHeritageOptions,
  onContinue,
  onSkip,
  selectedColor,
  continueButtonText = "Continue",
  skipText = "Skip for now",
  progressPercentage,
  onBack,
  onClose,
  initialSelectedId,
}: HeritageProps) => {
  const [selected, setSelected] = useState<HeritageItem | null>(
    initialSelectedId
      ? options.find((o) => o.id === initialSelectedId) ?? null
      : null
  );

  const handleSelectionChange = (item: HeritageItem | null) => {
    setSelected(item);
    onContinue?.(item?.id ?? "");
  };

  const handleContinue = () => {
    if (!selected) {
      return;
    }
    onContinue?.(selected.id);
  };

  const handleSkip = () => {
    onSkip?.();
  };

  const handleBack = () => {
    onBack?.();
  };

  const handleClose = () => {
    onClose?.();
  };

  const isContinueDisabled = selected === null;

  return (
    <PersonalisationScreenLayout
      header={
        <PersonalisationProgressBar
          progressPercentage={progressPercentage}
          onBack={handleBack}
          onClose={handleClose}
        />
      }
      footer={
        <>
          <Button
            style="primary"
            size="large"
            fullWidth
            disabled={isContinueDisabled}
            onClick={handleContinue}
          >
            {continueButtonText}
          </Button>

          <TextButton variant="secondary" onClick={handleSkip}>
            {skipText}
          </TextButton>
        </>
      }
    >
      <PersonalisationHeading size="sm">
        How would you describe your background?
      </PersonalisationHeading>

      <SingleChoiceTagPicker
        items={options}
        selectedId={selected?.id ?? null}
        onSelectionChangeRequest={handleSelectionChange}
        selectedColor={selectedColor}
        ariaLabel="Select your heritage"
      />
    </PersonalisationScreenLayout>
  );
};
